import { currentRelay } from './network';

/**
 * Media conversion with ffmpeg: in the desktop app (bundled ffmpeg, see
 * apps/desktop/src/media.ts) and in the browser version through the
 * streaming relay (apps/relay). Audio the player cannot decode (Dolby
 * AC3/E-AC3, DTS, TrueHD) becomes AAC; in browsers, MKV/AVI files (which
 * browsers can't open at all) are repackaged too. Video is never re-encoded.
 * On TVs everything here is "not available".
 */
export interface MediaProbe {
  durationSeconds: number | null;
  audioCodecs: string[];
  videoCodec: string | null;
  /** From the relay: picture height and HDR, for converting 4K video. */
  height?: number | null;
  hdr?: boolean;
  /** From the relay: subtitle tracks inside the file. */
  subtitles?: SubtitleInfo[];
}

/** A subtitle track inside a file; `text` tracks can be shown in browsers (picture subtitles can't). */
export interface SubtitleInfo {
  index: number;
  language: string | null;
  title: string | null;
  text: boolean;
}

/** Subtitle tracks written during a conversion (browser version, through the relay). */
export interface SubtitleConversion {
  session: string;
  tracks: SubtitleInfo[];
}

/** Picture conversion for 4K HEVC titles in browsers (relay with hardware encoding). */
export interface VideoConversion {
  height: number | null;
  hdr: boolean;
}

/** Audio codecs Chromium decodes itself (ffmpeg names). */
const SUPPORTED_AUDIO = new Set(['aac', 'mp3', 'opus', 'vorbis', 'flac', 'pcm_s16le', 'pcm_s24le', 'pcm_f32le', 'pcm_u8']);

/** Containers worth probing: they commonly carry Dolby or DTS audio. */
const PROBE_EXTENSIONS = new Set(['mkv', 'avi', 'ts', 'mov', 'wmv', 'flv', 'm2ts', 'mpg', 'mpeg', 'vob', 'divx', '']);

export function isDesktopApp(): boolean {
  return typeof location !== 'undefined' && location.protocol === 'app:';
}

/** Whether a converter may be available: the desktop app, or a browser using the relay. */
export function conversionPossible(): boolean {
  return isDesktopApp() || currentRelay() !== null;
}

/** Containers browsers can play directly; anything else needs repackaging in the browser version. */
const BROWSER_CONTAINERS = new Set(['mp4', 'm4v', 'mov', 'webm', 'm3u8', 'ts']);

/** Whether the file's container can't be played in a browser at all (MKV, AVI…). */
export function needsRemux(url: string): boolean {
  if (isDesktopApp() || currentRelay() === null) return false;
  return !BROWSER_CONTAINERS.has(extensionOf(url));
}

interface Capabilities {
  audioConversion: boolean;
  videoConversion: boolean;
  subtitles: boolean;
}

/** Known answers per converter. Failures are not remembered: the relay may be briefly unreachable. */
const capabilities = new Map<string, Capabilities>();

async function converterCapabilities(): Promise<Capabilities | null> {
  const relay = isDesktopApp() ? null : currentRelay();
  if (!isDesktopApp() && !relay) return null;
  const endpoint = relay ? `${relay.base}/capabilities` : 'viva-provider://capabilities/';
  const known = capabilities.get(endpoint);
  if (known) return known;
  try {
    const body = (await (await fetch(endpoint)).json()) as Partial<Capabilities>;
    const answer = { audioConversion: body.audioConversion === true, videoConversion: body.videoConversion === true, subtitles: body.subtitles === true };
    capabilities.set(endpoint, answer);
    return answer;
  } catch {
    // Unreachable for a moment: a relay converts sound unless it said otherwise; the desktop app can't be unreachable.
    return relay ? { audioConversion: true, videoConversion: false, subtitles: false } : null;
  }
}

/** Whether this build can convert sound and containers (desktop app with bundled ffmpeg, or a relay with ffmpeg). */
export async function audioConversionAvailable(): Promise<boolean> {
  return (await converterCapabilities())?.audioConversion ?? false;
}

/** Whether the relay can show the subtitles inside files (browser version). */
export async function subtitlesAvailable(): Promise<boolean> {
  return !isDesktopApp() && ((await converterCapabilities())?.subtitles ?? false);
}

/** Where the WebVTT written so far for one subtitle track of a conversion can be read. */
export function subtitlesUrl(session: string, index: number): string | null {
  const relay = currentRelay();
  return relay ? `${relay.base}/subtitles?t=${encodeURIComponent(relay.ticket)}&session=${session}&track=${index}` : null;
}

/** Whether the relay can turn 4K HEVC into 1080p H.264 for browsers. */
export async function videoConversionAvailable(): Promise<boolean> {
  return !isDesktopApp() && ((await converterCapabilities())?.videoConversion ?? false);
}

/**
 * Whether this browser decodes 4K HEVC itself (Android TV / Fire TV, Safari, Chrome and Edge with
 * hardware HEVC). Firefox is left out: it claims HEVC on some systems and then shows nothing.
 */
export function hevcPlayable(): boolean {
  if (typeof MediaSource === 'undefined' || typeof navigator === 'undefined' || /firefox/i.test(navigator.userAgent)) return false;
  // Main 10 at level 5.1 (most 4K files), then Main.
  return ['hvc1.2.4.L153.B0', 'hvc1.1.6.L153.B0'].some((codec) => MediaSource.isTypeSupported(`video/mp4; codecs="${codec}"`));
}

function extensionOf(url: string): string {
  try {
    const path = new URL(url).pathname;
    const last = path.slice(path.lastIndexOf('/') + 1);
    const dot = last.lastIndexOf('.');
    return dot > 0 ? last.slice(dot + 1).toLowerCase() : '';
  } catch {
    return '';
  }
}

export function shouldProbe(url: string): boolean {
  try {
    new URL(url);
  } catch {
    return false;
  }
  return PROBE_EXTENSIONS.has(extensionOf(url));
}

/**
 * Reads duration and codecs; null when that couldn't be asked (playback then proceeds normally),
 * 'unreadable' when the relay answered 502 "Probe failed" (see `afterProbe`).
 */
export async function probeMedia(url: string, signal: AbortSignal): Promise<MediaProbe | null | 'unreadable'> {
  try {
    const relay = isDesktopApp() ? null : currentRelay();
    const endpoint = relay
      ? `${relay.base}/probe?t=${encodeURIComponent(relay.ticket)}&url=${encodeURIComponent(url)}`
      : `viva-provider://probe/?url=${encodeURIComponent(url)}`;
    const response = await fetch(endpoint, { signal });
    if (response.status === 502) return 'unreadable';
    if (!response.ok) return null;
    return (await response.json()) as MediaProbe;
  } catch {
    return null;
  }
}

/**
 * What to do with a movie once its probe answered (#190): 'fail' when the relay could open nothing
 * there and the file can't play without converting it (MKV/AVI in a browser), since the conversion
 * would fail the same way: the player says so at once instead of loading for half a minute.
 * Otherwise the probe, or null to play as before. A 502 also comes from a probe that timed out or
 * a relay without ffmpeg, so a file the browser can play directly (MP4, TS…) is still tried, and the
 * desktop app (which plays files its own way) is never stopped.
 */
export function afterProbe(probed: MediaProbe | null | 'unreadable', url: string): MediaProbe | null | 'fail' {
  if (probed !== 'unreadable') return probed;
  return needsRemux(url) ? 'fail' : null;
}

/** The first audio track is what gets played; convert it unless Chromium can decode it. */
export function needsAudioConversion(probe: MediaProbe): boolean {
  const first = probe.audioCodecs[0];
  return first !== undefined && !SUPPORTED_AUDIO.has(first);
}

export function transcodeUrl(url: string, startSeconds: number, video?: VideoConversion, subtitles?: SubtitleConversion): string {
  const start = Math.max(0, Math.floor(startSeconds));
  const relay = isDesktopApp() ? null : currentRelay();
  if (relay) {
    const picture = video ? `&video=1080&height=${video.height ?? ''}&hdr=${video.hdr ? 1 : 0}` : '';
    const text = subtitles && subtitles.tracks.length > 0 ? `&subs=${subtitles.session}&subtracks=${subtitles.tracks.map((track) => track.index).join(',')}` : '';
    return `${relay.base}/transcode?t=${encodeURIComponent(relay.ticket)}&start=${start}${picture}${text}&url=${encodeURIComponent(url)}`;
  }
  return `viva-provider://transcode/?url=${encodeURIComponent(url)}&start=${start}`;
}

/** Display name for codec ids ("eac3" → "Dolby Digital Plus (E-AC-3)"). */
export function codecLabel(codec: string): string {
  switch (codec) {
    case 'ac3':
      return 'Dolby Digital (AC-3)';
    case 'eac3':
      return 'Dolby Digital Plus (E-AC-3)';
    case 'truehd':
      return 'Dolby TrueHD';
    case 'dts':
    case 'dca':
      return 'DTS';
    default:
      return codec.toUpperCase();
  }
}
